import { useState } from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { useShop } from "@/context/ShopContext";
import { api, img, money, errMsg, FREE_SHIPPING, SHIPPING_FEE } from "@/lib/api";
import useSeo from "@/lib/useSeo";

const FIELDS = [
  { k: "email", label: "Email address", type: "email", full: true },
  { k: "first_name", label: "First name" }, { k: "last_name", label: "Last name" },
  { k: "address1", label: "Address line 1", full: true }, { k: "address2", label: "Address line 2 (optional)", full: true, optional: true },
  { k: "city", label: "Town / City" }, { k: "postcode", label: "Postcode" },
  { k: "phone", label: "Phone (optional)", optional: true }, { k: "country", label: "Country" },
];

export default function Checkout() {
  useSeo("Checkout");
  const { items, subtotal, settings } = useShop();
  const [form, setForm] = useState({ country: "United Kingdom" });
  const [loading, setLoading] = useState(false);
  const shipping = subtotal >= FREE_SHIPPING || subtotal === 0 ? 0 : SHIPPING_FEE;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map(({ product_id, size, colour, quantity }) => ({ product_id, size, colour, quantity })),
        customer: form, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(errMsg(err));
      setLoading(false);
    }
  };

  if (!items.length) return (
    <div data-testid="checkout-empty" className="px-6 sm:px-16 py-32"><p className="font-serif-mm text-5xl">Your bag is <em className="text-[var(--mm-red)]">empty.</em></p><Link to="/shop" className="btn-mm mt-8">Shop the collection</Link></div>
  );

  if (settings?.payment_mode === "square_links") return (
    <div data-testid="checkout-square-links" className="px-6 sm:px-16 py-24 max-w-3xl">
      <p className="eyebrow text-[var(--mm-red)] mb-4">Checkout</p>
      <p className="font-serif-mm text-5xl leading-tight">Each piece is paid <em className="text-[var(--mm-red)]">securely with Square.</em></p>
      <p className="text-sm text-[var(--mm-muted)] mt-6">Open a piece below and press Buy now. Please add your size in the order note on the payment page.</p>
      <div className="mt-10 border-t border-[var(--mm-line)]">
        {items.map((it) => (
          <Link key={`${it.product_id}-${it.size}`} to={`/product/${it.slug}`} data-testid="checkout-square-item" className="flex items-center gap-4 py-4 border-b border-[var(--mm-line)] hover:text-[var(--mm-red)]">
            <img src={img(it.image)} alt="" className="w-14 aspect-[3/4] object-cover" />
            <span className="flex-1 text-sm">{it.name} · Size {it.size}</span>
            <span className="text-sm">{money(it.price)}</span>
          </Link>
        ))}
      </div>
    </div>
  );

  return (
    <div data-testid="checkout-page" className="grid lg:grid-cols-12 min-h-[80vh]">
      <form onSubmit={submit} className="lg:col-span-7 px-5 sm:px-10 lg:px-16 py-14">
        <p className="eyebrow text-[var(--mm-red)] mb-3">Secure checkout</p>
        <h1 className="font-serif-mm text-5xl mb-12">Delivery <em>details</em></h1>
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
          {FIELDS.map((f) => (
            <label key={f.k} className={f.full ? "sm:col-span-2" : ""}>
              <span className="eyebrow text-[var(--mm-muted)]">{f.label}</span>
              <input data-testid={`checkout-${f.k}-input`} type={f.type || "text"} required={!f.optional} value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} className="field-mm" />
            </label>
          ))}
        </div>
        <button data-testid="checkout-pay-button" disabled={loading} className="btn-mm w-full mt-12"><Lock size={14} /> {loading ? "Redirecting to secure payment…" : `Continue to payment · ${money(subtotal + shipping)}`}</button>
        <p className="text-xs text-[var(--mm-muted)] mt-4">You'll be taken to our secure payment page to pay by card, Apple Pay or Google Pay.</p>
      </form>
      <aside className="lg:col-span-5 bg-[var(--mm-sand)] px-5 sm:px-10 py-14">
        <p className="eyebrow mb-8">Order summary</p>
        <div className="space-y-5">
          {items.map((it) => (
            <div key={`${it.product_id}${it.size}${it.colour}`} data-testid="checkout-summary-item" className="flex gap-4">
              <div className="relative w-16 aspect-[3/4] bg-[var(--mm-cream)]"><img src={img(it.image)} alt="" className="w-full h-full object-cover" /><span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-[var(--mm-red)] text-[var(--mm-cream)] text-[10px] flex items-center justify-center">{it.quantity}</span></div>
              <div className="flex-1"><p className="text-sm font-semibold">{it.name}</p><p className="text-xs text-[var(--mm-muted)]">Size {it.size}{it.colour ? ` · ${it.colour}` : ""}</p></div>
              <p className="text-sm">{money(it.price * it.quantity)}</p>
            </div>
          ))}
        </div>
        <div className="border-t border-[var(--mm-line)] mt-8 pt-6 space-y-3 text-sm">
          <div className="flex justify-between"><span>Subtotal</span><span data-testid="checkout-subtotal">{money(subtotal)}</span></div>
          <div className="flex justify-between"><span>UK delivery</span><span data-testid="checkout-shipping">{shipping ? money(shipping) : "Complimentary"}</span></div>
          <div className="flex justify-between text-lg pt-3 border-t border-[var(--mm-line)]"><span className="font-serif-mm text-2xl">Total</span><span data-testid="checkout-total" className="font-semibold">{money(subtotal + shipping)}</span></div>
          <p className="text-xs text-[var(--mm-muted)]">Prices include VAT where applicable.</p>
        </div>
      </aside>
    </div>
  );
}
